import React, { useState } from "react";
import styles from './Deployment.module.scss'
import Icon from "../../../../../../components/Icon";
import Button from "../../../../../../components/ripple/Button";
import { DEPLOYMENT_STATUSES } from "../../../../../../settings/content/deployment";
import FoldingContent from "../../../../../../components/folding/FoldingContent";
import StatusDetails from "./components/StatusDetails";
import InstanceDetails from "./components/InstanceDetails";
import AppsDetails from "./components/AppsDetails";
import DetailsLayout from "./layouts/DetailsLayout";
import BuilderDeploymentLogs from "./components/BuilderDeploymentLogs";
import { DEBUG } from "../../../../../../settings/global";

function getFullDate(date) {

    let dateUTC = new Date(date);
    let month = dateUTC.toLocaleString('default', {month: 'short'});
    let day = dateUTC.getDate();
    let year = dateUTC.getFullYear();

    return day + ' ' + month + ', ' + year;
}

function formatDeploymentDate(updated) {

    let updatedDate = null;
    const parsedCurrentTime = new Date();

    if (!!updated) {
        let updatedUTC = new Date(updated);
        let difference = (parsedCurrentTime.getTime() - updatedUTC.getTime());
        // difference counting user's timezone offset in milliseconds
        difference = difference + parsedCurrentTime.getTimezoneOffset() * 1000 * 60;
        const differenceInMinutes = difference / (1000 * 60);
        const differenceInHours = difference / (1000 * 3600);
        const differenceInDays = difference / (1000 * 3600 * 24);

        switch (true) {
            case differenceInMinutes < 1 :
                updatedDate = 'within a minute ago';
                break;
            case differenceInMinutes < 60 :
                updatedDate = `${Math.ceil(differenceInMinutes)} minutes ago`;
                break;
            case differenceInHours < 1 :
                updatedDate = 'an hour ago';
                break;
            case differenceInDays < 1 :
                updatedDate = `${Math.ceil(differenceInHours)} hours ago`;
                break;
            case differenceInDays > 1 && differenceInDays < 30:
                updatedDate = `${Math.floor(differenceInDays)} ${differenceInDays < 2 ? 'day' : 'days'} ago`;
                break;
            default:
                updatedDate = getFullDate(updated);
        }
    }

    return updatedDate
}


function Deployment({deployment, deploymentLoading, clouds, ssl}) {

    const [folded, setFolded] = useState(true);
    const [foldedStatus, setFoldedStatus] = useState(true);

    const cloud = clouds.find(c => c.code === deployment.request_dump.provider);
    const status = {
        icon: DEPLOYMENT_STATUSES[deployment.status]?.icon || 'fas fa-times',
        title: DEPLOYMENT_STATUSES[deployment.status]?.title || 'Unknown status',
        color: DEPLOYMENT_STATUSES[deployment.status]?.color || 'Error',
    };

    return (
        <div className={styles.item} style={{backgroundColor: 'var(--colorBackgroundContent)'}}>
            <div className={styles.item__head}>
                <div className={styles.item__head__status} title={status.title}>
                    <Button
                        className={styles.item__head__status__button}
                        onClick={() => setFoldedStatus(!foldedStatus)}
                        icon={foldedStatus ? status.icon : 'fas fa-times'}
                        displayType='text'
                        visuals={{colorBasic: foldedStatus ? [`var(--colorFont${status.color})`] : [`var(--colorAccentBasic)`]}}
                    />
                </div>
                <div className={styles.item__head__logo}>
                    <Icon
                        className={styles.item__head__logo__image}
                        icon={cloud.icon}
                        fallbackImages={['/images/no-app-icon.svg', '/images/no-app-icon-dark.svg']}
                        alt={cloud.name || cloud.code}
                    />
                </div>
                <div className={styles.item__head__content}>
                    <p className={styles.item__head__content__name}>
                        {cloud.name || cloud.code || deployment.request_dump.provider}
                    </p>
                    <time className={styles.item__head__content__time} title={deployment._updated}>
                        {formatDeploymentDate(deployment._updated)}
                    </time>
                </div>
                <div className={styles.item__head__actions}>
                    <Button
                        className={styles.item__head__actions__action}
                        icon={`fas fa-chevron-${folded ? 'down' : 'up'}`}
                        displayType='text'
                        onClick={() => {
                            setFolded(!folded);
                            setFoldedStatus(!folded);
                        }}
                    />
                    <Button
                        className={styles.item__head__actions__action}
                        icon='fas fa-ellipsis-v'
                        displayType='text'
                    />
                </div>
            </div>
            <div className={styles.item__fold}>
                <div
                    className={styles.details}
                    style={{
                        borderRadius: !foldedStatus && folded ? 'var(--borderRadiusContainerInnerEdge)' : undefined,
                        marginBottom: (foldedStatus || !foldedStatus && folded) ? '0' : undefined,
                    }}
                >
                    <FoldingContent folded={foldedStatus}>
                        <DetailsLayout
                            title={status.title}
                            icon={status.icon}
                            iconColor={`var(--colorFont${status.color})`}
                            iconOpacity={1}
                            titleWeight='var(--fontWeightSecondaryBold)'
                        >
                            <StatusDetails deployment={deployment} ssl={ssl}/>
                        </DetailsLayout>
                    </FoldingContent>
                </div>
                <div
                    className={styles.details}
                    style={{
                        borderRadius: foldedStatus && !folded
                            ? 'var(--borderRadiusContainerInnerEdge) var(--borderRadiusContainerInnerEdge) 0 0'
                            : undefined,
                        marginBottom: folded ? '0' : undefined,
                    }}
                >
                    <FoldingContent folded={folded}>
                        <DetailsLayout title='Instance' folded={true} icon={`fas fa-server`}>
                            <InstanceDetails deployment={deployment}/>
                        </DetailsLayout>
                    </FoldingContent>
                </div>
                {
                    !!DEBUG &&
                    <div
                        className={styles.details}
                        style={{marginBottom: folded ? '0' : undefined}}
                    >
                        <FoldingContent folded={folded}>
                            <DetailsLayout title='Apps' icon='fas fa-cubes' folded={true}>
                                <AppsDetails deployment={deployment}/>
                            </DetailsLayout>
                        </FoldingContent>
                    </div>
                }
                <div
                    className={styles.details}
                    style={{marginBottom: folded ? '0' : undefined}}
                >
                    <FoldingContent folded={folded}>
                        <BuilderDeploymentLogs
                            deployment={deployment}
                            deploymentLoading={deploymentLoading}
                            custom
                        />
                    </FoldingContent>
                </div>
            </div>
        </div>
    )
}

export default Deployment